import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { Building2Icon } from "lucide-react";

import type { WorkspaceAdminResponse } from "../../shared/schemas";
import { navigateToWorkspace } from "../api/client";
import { meQuery } from "../api/queries";
import { AuthenticatedShell } from "../components/AuthenticatedShell";
import { OfflineState, useOnlineStatus } from "../components/OfflineState";
import { useShellViewMode } from "../components/ShellViewModeProvider";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { Skeleton } from "../components/ui/skeleton";
import { resolveShellOrganizationId, resolveShellViewMode } from "../lib/organization-context";
import { cn } from "../lib/utils";
import { workspaceRoute } from "../router";
import { WorkspaceAppearancePanel } from "./workspace/WorkspaceAppearancePanel";
import { WorkspaceSettingsPanel } from "./workspace/WorkspaceSettingsPanel";
import { WorkspaceUsersPanel } from "./workspace/WorkspaceUsersPanel";
import { useWorkspaceController } from "./workspace/useWorkspaceController";

type WorkspaceTab = "users" | "settings" | "appearance";

function WorkspaceTabs({
  tab,
  onChange,
}: {
  tab: WorkspaceTab;
  onChange: (tab: WorkspaceTab) => void;
}) {
  return (
    <nav aria-label="Workspace settings" className="mt-7 flex gap-7 border-b">
      {(["users", "settings", "appearance"] as const).map((item) => (
        <button
          aria-current={tab === item ? "page" : undefined}
          className={cn(
            "relative min-h-11 border-0 bg-transparent px-0 font-sans text-sm font-semibold text-muted-foreground outline-none transition-[color] duration-150 focus-visible:shadow-[var(--focus-ring)]",
            tab === item &&
              "text-link after:absolute after:right-0 after:bottom-[-1px] after:left-0 after:h-[2.5px] after:bg-link",
          )}
          key={item}
          type="button"
          onClick={() => {
            onChange(item);
          }}
        >
          {item === "users" ? "Users" : item === "settings" ? "Workspace settings" : "Appearance"}
        </button>
      ))}
    </nav>
  );
}

function WorkspaceQueryState({ error }: { error: boolean }) {
  if (!error) {
    return (
      <div className="grid gap-4 py-8">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  return (
    <div className="py-8">
      <Empty role="alert">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Building2Icon aria-hidden="true" />
          </EmptyMedia>
          <EmptyTitle>Workspace settings are not available.</EmptyTitle>
          <EmptyDescription>
            Only workspace administrators can manage users and settings.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

type WorkspacePanelProps = {
  controller: ReturnType<typeof useWorkspaceController>;
  isOnline: boolean;
  organizationId: string;
  tab: WorkspaceTab;
};

type WorkspaceContentProps = Omit<WorkspacePanelProps, "tab"> & {
  workspace: WorkspaceAdminResponse;
};

function WorkspaceUsersTab({
  controller,
  isOnline,
  organizationId,
  workspace,
}: WorkspaceContentProps) {
  return (
    <WorkspaceUsersPanel
      isOnline={isOnline}
      mutationMessage={controller.mutationMessage}
      organizationId={organizationId}
      successMessage={controller.edits.successMessage}
      workspace={workspace}
      onCreated={async (result) => {
        controller.edits.setSuccessMessage(
          result.accountCreated
            ? `${result.member.user.displayName} can now sign in to this workspace.`
            : `${result.member.user.displayName} was added to this workspace.`,
        );
        await controller.resources.refresh();
      }}
      onUpdated={controller.resources.refresh}
    />
  );
}

function WorkspaceSettingsTab({ controller, isOnline, organizationId }: WorkspaceContentProps) {
  return (
    <WorkspaceSettingsPanel
      canEditSubdomain={controller.resources.address.data?.canEdit ?? false}
      isOnline={isOnline}
      nameError={controller.edits.nameError}
      namePending={controller.mutations.name.isPending}
      subdomain={controller.workspaceSubdomain}
      subdomainError={controller.edits.subdomainError}
      subdomainPending={controller.mutations.subdomain.isPending}
      workspaceName={controller.workspaceName}
      onNameChange={(value) => {
        controller.edits.setNameEdit({ organizationId, value });
        controller.edits.setNameError("");
      }}
      onSaveName={controller.saveName}
      onSaveSubdomain={controller.saveSubdomain}
      onSubdomainChange={(value) => {
        controller.edits.setSubdomainEdit({ organizationId, value });
        controller.edits.setSubdomainError("");
      }}
    />
  );
}

function WorkspaceAppearanceTab({
  controller,
  isOnline,
  organizationId,
  workspace,
}: WorkspaceContentProps) {
  return (
    <WorkspaceAppearancePanel
      colorError={controller.edits.colorError}
      colorPending={controller.mutations.color.isPending}
      faviconPending={
        controller.mutations.favicon.isPending || controller.mutations.removeFavicon.isPending
      }
      isOnline={isOnline}
      logoPending={controller.mutations.logo.isPending || controller.mutations.removeLogo.isPending}
      mutationMessage={controller.mutationMessage}
      organizationId={organizationId}
      primaryColor={controller.primaryColor}
      primaryColorPickerValue={controller.pickerColor}
      workspace={workspace}
      onColorChange={(value) => {
        controller.edits.setColorEdit({ organizationId, value });
        controller.edits.setColorError("");
      }}
      onFaviconRemove={() => {
        controller.mutations.removeFavicon.mutate();
      }}
      onFaviconUpload={(file) => {
        controller.mutations.favicon.mutate(file);
      }}
      onLogoRemove={() => {
        controller.mutations.removeLogo.mutate();
      }}
      onLogoUpload={(file) => {
        controller.mutations.logo.mutate(file);
      }}
      onResetColor={() => {
        controller.edits.setColorError("");
        controller.mutations.color.mutate(null);
      }}
      onSaveColor={controller.saveColor}
    />
  );
}

function WorkspacePanel(props: WorkspacePanelProps) {
  const workspace = props.controller.resources.workspace.data;
  if (!workspace) {
    return <WorkspaceQueryState error={props.controller.resources.workspace.isError} />;
  }
  const contentProps = { ...props, workspace };
  if (props.tab === "users") return <WorkspaceUsersTab {...contentProps} />;
  if (props.tab === "settings") return <WorkspaceSettingsTab {...contentProps} />;
  return <WorkspaceAppearanceTab {...contentProps} />;
}

export function WorkspacePage() {
  const isOnline = useOnlineStatus();
  const me = useQuery({ ...meQuery, enabled: isOnline });
  const search = workspaceRoute.useSearch();
  const navigate = useNavigate({ from: "/workspace" });
  const { consultantViewMode } = useShellViewMode();
  const organizationId = resolveShellOrganizationId(me.data, search.org);
  const organization = me.data?.organizations.find(({ id }) => id === organizationId);
  const canManage =
    resolveShellViewMode(me.data?.user, organization, consultantViewMode) === "admin";
  const controller = useWorkspaceController(organizationId, isOnline, canManage);
  if (me.data && organizationId && search.org !== organizationId) {
    return <Navigate replace search={{ org: organizationId, tab: search.tab }} to="/workspace" />;
  }
  return (
    <AuthenticatedShell
      activeNavigationItem="workspace"
      activeOrganizationId={organizationId}
      requiresAdminView
      onOrganizationChange={(nextId) => {
        navigateToWorkspace(
          nextId,
          `/workspace?org=${encodeURIComponent(nextId)}&tab=${search.tab}`,
        );
      }}
    >
      <main className="workspace-main mx-auto w-full max-w-[1240px]" id="main-content">
        {!isOnline ? (
          <OfflineState
            className="mb-7"
            description="Reconnect to view or change workspace settings."
            variant="banner"
          />
        ) : null}
        <header className="max-w-3xl">
          <p className="mono-label mb-3">Workspace administration</p>
          <h1 className="display-title mb-3">Workspace</h1>
          <p className="m-0 max-w-2xl text-pretty text-muted-foreground">
            Manage the people, tool access, workspace details, and appearance.
          </p>
        </header>
        <WorkspaceTabs
          tab={search.tab}
          onChange={(tab) => void navigate({ search: { org: organizationId, tab } })}
        />
        <WorkspacePanel
          controller={controller}
          isOnline={isOnline}
          organizationId={organizationId}
          tab={search.tab}
        />
      </main>
    </AuthenticatedShell>
  );
}
